import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Wallet, Banknote, ShieldCheck, ExternalLink, CheckCircle2, Clock, XCircle,
  Send, AlertTriangle, Loader2, ArrowUpRight,
} from 'lucide-react';

const inr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usdt = (v) => `${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })} USDT`;
const TRON_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const explorer = (h) => `https://tronscan.org/#/transaction/${h}`;

const STATUS = {
  pending_approval: { label: 'Pending Approval', cls: 'bg-amber-500/15 text-amber-600 border-amber-500/30', icon: Clock },
  approved: { label: 'Approved', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: CheckCircle2 },
  processing: { label: 'Processing', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: Loader2 },
  sent: { label: 'Sent', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30', icon: Send },
  completed: { label: 'Completed', cls: 'bg-green-500/15 text-green-600 border-green-500/30', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-red-500/15 text-red-600 border-red-500/30', icon: XCircle },
  failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-600 border-red-500/30', icon: XCircle },
};

const StatusBadge = ({ s }) => {
  const m = STATUS[s] || STATUS.pending_approval;
  return <Badge variant="outline" className={`rounded-sm text-[10px] ${m.cls}`} data-testid={`wd-status-${s}`}>{m.label.toUpperCase()}</Badge>;
};

const Timeline = ({ w }) => {
  const rejected = w.status === 'rejected' || w.status === 'failed';
  const steps = [
    { key: 'submitted', label: 'Submitted', at: w.created_at, done: true },
    { key: 'approved', label: rejected ? 'Rejected' : 'Approved / Processing', at: w.reviewed_at, done: ['approved', 'processing', 'sent', 'completed'].includes(w.status), fail: rejected },
    { key: 'completed', label: 'Completed (on-chain)', at: w.completed_at, done: w.status === 'completed' },
  ];
  return (
    <div className="space-y-3" data-testid="wd-timeline">
      {steps.map((s, i) => (
        <div key={s.key} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs ${s.fail ? 'bg-red-500 text-white' : s.done ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>
              {s.fail ? <XCircle className="h-3.5 w-3.5" /> : s.done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
            </div>
            {i < steps.length - 1 && <div className={`w-0.5 flex-1 min-h-[18px] ${s.done && !s.fail ? 'bg-green-500/40' : 'bg-border'}`} />}
          </div>
          <div className="pb-2">
            <p className={`text-sm font-medium ${s.fail ? 'text-red-600' : ''}`}>{s.label}</p>
            {s.at && <p className="text-xs text-muted-foreground">{new Date(s.at).toLocaleString('en-IN')}</p>}
            {s.key === 'approved' && rejected && w.rejection_reason && <p className="text-xs text-red-600 mt-0.5">{w.rejection_reason}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function ResellerWithdrawalsPage() {
  const { api } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [addr, setAddr] = useState('');
  const [confirmAddr, setConfirmAddr] = useState('');
  const [showAddr, setShowAddr] = useState(false);

  const [amount, setAmount] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const [w, h] = await Promise.all([api.get('/reseller/wallet'), api.get('/reseller/withdrawals')]);
      setWallet(w.data);
      setWithdrawals(h.data.withdrawals || []);
    } catch { toast.error('Failed to load withdrawals'); }
    finally { setLoading(false); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const saveAddress = async () => {
    if (!TRON_RE.test(addr.trim())) { toast.error('Enter a valid TRC20 address (starts with T, 34 chars)'); return; }
    if (addr.trim() !== confirmAddr.trim()) { toast.error('Address confirmation does not match'); return; }
    setBusy(true);
    try {
      await api.put('/reseller/withdrawal-address', { address: addr.trim(), confirm_address: confirmAddr.trim(), network: 'TRC20' });
      toast.success('Withdrawal address updated');
      setShowAddr(false); setAddr(''); setConfirmAddr('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update address'); }
    finally { setBusy(false); }
  };

  const submitWithdrawal = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setBusy(true);
    try {
      await api.post('/reseller/withdrawals', { amount_usdt: amt });
      toast.success('Withdrawal request submitted');
      setShowForm(false); setAmount('');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit withdrawal'); }
    finally { setBusy(false); }
  };

  if (loading || !wallet) {
    return <div className="space-y-6"><Skeleton className="h-28" /><Skeleton className="h-64" /></div>;
  }

  const w = wallet.wallet;
  const rate = w.usdt_rate;
  const amt = parseFloat(amount) || 0;
  const feePct = wallet.withdrawal_fee_percentage || 0;
  const feeFixed = wallet.withdrawal_fee_fixed_usdt || 0;
  const fee = amt > 0 ? amt * feePct / 100 + feeFixed : 0;
  const net = Math.max(0, amt - fee);
  const estInr = amt * rate;

  return (
    <div className="space-y-6" data-testid="reseller-withdrawals-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">Withdrawals</h1>
        <p className="text-muted-foreground text-sm">Withdraw your earnings in USDT (TRC20). Settlements are reviewed by AwadhPay.</p>
      </div>

      {/* Balance + address + withdraw */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Wallet className="h-4 w-4" />Available Balance</p>
            <p className="text-2xl font-bold font-heading mt-2" data-testid="available-usdt">{usdt(w.available_usdt)}</p>
            <p className="text-muted-foreground text-sm">≈ {inr(w.available_inr)}</p>
            <p className="text-xs text-muted-foreground mt-3">Rate: {inr(rate)} / USDT · Min: {wallet.min_withdrawal_usdt} USDT</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Withdrawal Address</p>
            {wallet.withdrawal_address ? (
              <>
                <p className="font-mono text-xs break-all mt-3 bg-muted/50 rounded px-2 py-1.5" data-testid="saved-address">{wallet.withdrawal_address}</p>
                <Badge variant="outline" className="mt-2 rounded-sm text-[10px]">TRON (TRC20)</Badge>
              </>
            ) : (
              <p className="text-sm text-muted-foreground mt-3">No address set. Add a TRC20 address to withdraw.</p>
            )}
            <Button variant="outline" size="sm" className="w-full mt-3 rounded-sm" onClick={() => setShowAddr(true)} data-testid="manage-address-btn">
              {wallet.withdrawal_address ? 'Change Address' : 'Add Address'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex flex-col h-full">
            <p className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Banknote className="h-4 w-4" />Withdraw</p>
            <p className="text-sm text-muted-foreground mt-3 flex-1">
              {!wallet.withdrawal_address ? 'Add a TRC20 address first.'
                : wallet.has_active_withdrawal ? 'You have a withdrawal in progress.'
                : w.available_usdt < wallet.min_withdrawal_usdt ? `Minimum withdrawal is ${wallet.min_withdrawal_usdt} USDT.`
                : 'Request a withdrawal to your saved TRC20 address.'}
            </p>
            <Button className="w-full mt-3 rounded-sm" disabled={!wallet.can_withdraw} onClick={() => setShowForm(true)} data-testid="open-withdraw-btn">
              <ArrowUpRight className="w-4 h-4 mr-2" />Withdraw Earnings
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* History */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b"><p className="font-heading font-semibold">Withdrawal History</p></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="py-3 px-4">ID</th>
                  <th className="py-3 px-4">Amount</th>
                  <th className="py-3 px-4">Rate</th>
                  <th className="py-3 px-4">INR Value</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4 text-right">TX</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.length ? withdrawals.map((x) => (
                  <tr key={x.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setDetail(x)} data-testid={`wd-row-${x.withdrawal_code}`}>
                    <td className="py-3 px-4 font-mono text-xs font-semibold text-primary">{x.withdrawal_code}</td>
                    <td className="py-3 px-4 font-mono">{usdt(x.amount_usdt)}</td>
                    <td className="py-3 px-4 font-mono text-xs">{inr(x.exchange_rate)}</td>
                    <td className="py-3 px-4 font-mono">{inr(x.amount_inr)}</td>
                    <td className="py-3 px-4"><StatusBadge s={x.status} /></td>
                    <td className="py-3 px-4 text-xs">{new Date(x.created_at).toLocaleDateString('en-IN')}</td>
                    <td className="py-3 px-4 text-right">
                      {x.tx_hash ? (
                        <a href={explorer(x.tx_hash)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-primary inline-flex items-center gap-1 text-xs" data-testid={`tx-link-${x.withdrawal_code}`}>
                          View <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} className="text-center py-10 text-muted-foreground">No withdrawals yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Address dialog */}
      <Dialog open={showAddr} onOpenChange={setShowAddr}>
        <DialogContent data-testid="address-dialog">
          <DialogHeader>
            <DialogTitle>USDT Withdrawal Address</DialogTitle>
            <DialogDescription>Only the TRON (TRC20) network is supported. Double-check the address — transfers are irreversible.</DialogDescription>
          </DialogHeader>
          {wallet.withdrawal_address && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 flex gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Changing your address does not affect withdrawals already submitted — they keep the original address.
            </div>
          )}
          <div className="space-y-3 py-1">
            <div><Label className="text-xs uppercase tracking-wider">TRC20 Address</Label>
              <Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="T..." className="rounded-sm font-mono text-sm" data-testid="address-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider">Confirm Address</Label>
              <Input value={confirmAddr} onChange={(e) => setConfirmAddr(e.target.value)} placeholder="T..." className="rounded-sm font-mono text-sm" data-testid="confirm-address-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddr(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={saveAddress} disabled={busy} className="rounded-sm" data-testid="save-address-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Save Address</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle>Withdraw Earnings</DialogTitle>
            <DialogDescription>Available: {usdt(w.available_usdt)} · Minimum: {wallet.min_withdrawal_usdt} USDT</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs uppercase tracking-wider">Amount (USDT)</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`${wallet.min_withdrawal_usdt}`} className="rounded-sm" data-testid="withdraw-amount-input" />
            </div>
            <div className="rounded-lg border p-3 text-sm space-y-1.5 bg-muted/30">
              <div className="flex justify-between"><span className="text-muted-foreground">Network</span><span>TRON (TRC20)</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Destination</span><span className="font-mono text-xs truncate max-w-[200px]">{wallet.withdrawal_address}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Rate</span><span className="font-mono">{inr(rate)} / USDT</span></div>
              {fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Withdrawal Fee</span><span className="font-mono">{usdt(fee)}</span></div>}
              {fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">You Receive</span><span className="font-mono">{usdt(net)}</span></div>}
              <div className="flex justify-between border-t pt-1.5 font-semibold"><span>Estimated INR Value</span><span className="font-mono" data-testid="withdraw-est-inr">{inr(estInr)}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={submitWithdrawal} disabled={busy || amt < wallet.min_withdrawal_usdt || amt > w.available_usdt} className="rounded-sm" data-testid="submit-withdraw-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="wd-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">Withdrawal <span className="font-mono text-primary text-sm">{detail?.withdrawal_code}</span></DialogTitle>
            <DialogDescription>Full withdrawal details and status timeline.</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div><StatusBadge s={detail.status} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-xs text-muted-foreground">Amount</p><p className="font-mono">{usdt(detail.amount_usdt)}</p></div>
                <div><p className="text-xs text-muted-foreground">INR Value</p><p className="font-mono">{inr(detail.amount_inr)}</p></div>
                <div><p className="text-xs text-muted-foreground">Rate (snapshot)</p><p className="font-mono">{inr(detail.exchange_rate)}</p></div>
                <div><p className="text-xs text-muted-foreground">Network</p><p>TRON (TRC20)</p></div>
              </div>
              <div><p className="text-xs text-muted-foreground">Destination</p><p className="font-mono text-xs break-all bg-muted/50 rounded px-2 py-1">{detail.destination_address}</p></div>
              {detail.tx_hash && (
                <div><p className="text-xs text-muted-foreground">Transaction Hash</p>
                  <a href={explorer(detail.tx_hash)} target="_blank" rel="noreferrer" className="text-primary inline-flex items-center gap-1 font-mono text-xs break-all" data-testid="detail-tx-link">
                    {detail.tx_hash} <ExternalLink className="h-3 w-3 shrink-0" />
                  </a></div>
              )}
              <div className="pt-2 border-t"><p className="text-xs text-muted-foreground mb-3">Status Timeline</p><Timeline w={detail} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setDetail(null)} className="rounded-sm">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
